import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, Stack, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  useCloseAccessWindow, useMachineEvents, useMyPermissions, useOpenAccessWindow,
  useOpenAccessWindows, useReviewMachineEvent,
} from '../../api/queries';
import type { MachineEventRow } from '../../api/types';
import { useAlert, useConfirm, usePrompt } from '../../components/dialogs/DialogProvider';
import { fmtDate, fmtDateTime, fmtOrEmDash } from '../../utils/format';

/** ISO YYYY-MM-DD for `d` days ago (local). */
function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function severityColor(s: string): 'default' | 'warning' | 'error' {
  return s === 'alarm' ? 'error' : s === 'warning' ? 'warning' : 'default';
}

/**
 * Machine Event Pipeline (0384) — SAS machine events for this machine, by
 * gaming day. Raised (issue/alarm) events carry a Review action
 * (meters.audit-ack); events recorded inside an authorized collection/
 * maintenance window are marked so a door-open during an authorized stacker
 * clear reads differently from an unexplained one.
 */
export function MachineEventsTab({ machineId, siteId }: { machineId: string; siteId?: string }) {
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(isoDaysAgo(0));
  const q = useMachineEvents(machineId, from, to);
  const perms = useMyPermissions();
  const canAck = !!perms.data?.codes.includes('meters.audit-ack');
  const review = useReviewMachineEvent();
  const confirm = useConfirm();
  const alert = useAlert();

  const rangeError = from > to ? 'From date must be on or before To date.' : null;
  const rows = useMemo(() => q.data ?? [], [q.data]);

  async function onReview(e: MachineEventRow) {
    const ok = await confirm({
      title: 'Mark event reviewed',
      message: `Mark "${e.displayName}" (${e.sasCode}) on ${fmtDate(e.gamingDay)} as reviewed?`,
      confirmLabel: 'Mark reviewed',
    });
    if (!ok) return;
    try {
      await review.mutateAsync(e.id);
    } catch (err) {
      await alert({
        title: 'Could not review',
        message: (err as { message?: string })?.message ?? 'Failed to mark the event reviewed.',
        severity: 'error',
      });
    }
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        SAS events reported by this machine, stored per gaming day. Alarms and issues need review;
        events inside an authorized collection/maintenance window are recorded but not raised.
      </Typography>

      {siteId && <CollectionModeControl siteId={siteId} machineId={machineId} />}

      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          label="From" type="date" size="small" value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={!!rangeError} inputProps={{ 'aria-label': 'from date' }}
        />
        <TextField
          label="To" type="date" size="small" value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          error={!!rangeError} inputProps={{ 'aria-label': 'to date' }}
        />
        <Tooltip title="Refresh events">
          <span>
            <IconButton size="small" aria-label="refresh events" disabled={q.isFetching}
              onClick={() => void q.refetch()}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {q.isFetching && <CircularProgress size={18} />}
      </Stack>

      {rangeError && <Alert severity="warning">{rangeError}</Alert>}
      {q.isError && !rangeError && (
        <Alert severity="error">
          {(q.error as { message?: string })?.message ?? 'Failed to load machine events.'}
        </Alert>
      )}

      {!rangeError && !q.isError && rows.length === 0 && !q.isLoading && (
        <Typography variant="body2" color="text.secondary">No events in this range.</Typography>
      )}

      {rows.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="machine events" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Raised</TableCell>
                <TableCell>Gaming day</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Event</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Review</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.id} hover>
                  <TableCell>{fmtDateTime(e.raisedAt)}</TableCell>
                  <TableCell>{fmtDate(e.gamingDay)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{e.sasCode}</TableCell>
                  <TableCell>
                    {e.displayName}
                    {e.accessWindowId && (
                      <Tooltip title="Raised inside an authorized collection/maintenance window — recorded, not alarmed.">
                        <Chip size="small" variant="outlined" label="authorized" sx={{ ml: 1 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell><Chip size="small" color={severityColor(e.severity)} label={e.severity} /></TableCell>
                  <TableCell>
                    {e.raiseLevel === 'none' ? (
                      <Typography variant="body2" color="text.secondary">{fmtOrEmDash(null)}</Typography>
                    ) : e.reviewedAt ? (
                      <Chip size="small" label={e.reviewedByName ? `Reviewed by ${e.reviewedByName}` : 'Reviewed'} />
                    ) : canAck ? (
                      <Button size="small" variant="outlined" color="warning"
                        disabled={review.isPending} onClick={() => void onReview(e)}>
                        Review
                      </Button>
                    ) : (
                      <Chip size="small" color="warning" label="Needs review" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        {fmtOrEmDash(rows.length)} event(s) shown.
      </Typography>
    </Stack>
  );
}

/**
 * Collection/maintenance mode — opens an authorized-access window covering
 * this machine (owner design decision #2: context via windows, not a rules
 * engine). While a window is open, door/stacker events are recorded with the
 * window reference instead of raising alarms. machines.edit-gated, audited
 * server-side, auto-expires.
 */
function CollectionModeControl({ siteId, machineId }: { siteId: string; machineId: string }) {
  const perms = useMyPermissions();
  const canEdit = !!perms.data?.codes.includes('machines.edit');
  const windowsQ = useOpenAccessWindows(canEdit ? siteId : null);
  const openWindow = useOpenAccessWindow();
  const closeWindow = useCloseAccessWindow();
  const prompt = usePrompt();
  const alert = useAlert();

  if (!canEdit) return null;

  const active = (windowsQ.data ?? []).find((w) => w.machineId === machineId || w.machineId === null);

  async function onOpen() {
    const reason = await prompt({
      title: 'Open collection/maintenance mode',
      message: 'Door and stacker events for this machine will be recorded but not alarmed for the next 30 minutes. Reason:',
      confirmLabel: 'Open window',
    });
    if (!reason) return;
    try {
      await openWindow.mutateAsync({ siteId, machineId, reason, minutes: 30 });
    } catch (e) {
      await alert({
        title: 'Could not open window',
        message: (e as { message?: string })?.message ?? 'Failed to open the access window.',
        severity: 'error',
      });
    }
  }

  async function onClose(windowId: string) {
    try {
      await closeWindow.mutateAsync(windowId);
    } catch (e) {
      await alert({
        title: 'Could not close window',
        message: (e as { message?: string })?.message ?? 'Failed to close the access window.',
        severity: 'error',
      });
    }
  }

  return active ? (
    <Alert
      severity="info"
      action={(
        <Button color="inherit" size="small" disabled={closeWindow.isPending}
          onClick={() => void onClose(active.id)}>
          End now
        </Button>
      )}
    >
      Collection/maintenance mode is OPEN ({active.reason}) until {fmtDateTime(active.expiresAt)}
      {active.openedByName ? ` — opened by ${active.openedByName}` : ''}.
    </Alert>
  ) : (
    <Box>
      <Button size="small" variant="outlined" disabled={openWindow.isPending} onClick={() => void onOpen()}>
        Open collection/maintenance mode
      </Button>
    </Box>
  );
}
